import BanIcon from '@heroicons/react/outline/BanIcon';
import CheckIcon from '@heroicons/react/outline/CheckIcon';
import ChevronDownIcon from '@heroicons/react/outline/ChevronDownIcon';
import UsersIcon from '@heroicons/react/outline/UsersIcon';
import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import {
  getApprovalById,
  getApprovals,
  updateApproval,
} from '../../../api/approvals';
import {
  GetCommentParams,
  getPartnerProposalComments,
  postComment,
} from '../../../api/comments';
import {
  getDeliverable,
  getMilestone,
  getTask,
  updateTdm,
} from '../../../api/tdm';
import Comments from '../../../components/Comments';
import {
  LayoutCard,
  LayoutCardContent,
  LayoutCardTitle,
} from '../../../components/LayoutCard';
import { TdmInfo, TdmInfoGroup } from '../../../components/Tdm/TdmInfo';
import {
  TdmLayout,
  TdmLeftLayout,
  TdmRightLayout,
} from '../../../components/Tdm/TdmLayout';
import { TdmLead } from '../../../components/Tdm/TdmLead';
import Badge from '../../../components/UI/Badge';
import Button from '../../../components/UI/Button';
import EditIconButton from '../../../components/UI/Button/EditIconButton';
import DropDown, { DropDownMenuItem } from '../../../components/UI/DropDown';
import { HookForm } from '../../../components/UI/HookForm/Form';
import { HookInput } from '../../../components/UI/HookForm/Form/Input';
import { HookReactSingleSelect } from '../../../components/UI/HookForm/Form/ReactSelect';
import { HookTextarea } from '../../../components/UI/HookForm/Form/TextArea';
import { HookFormProvider } from '../../../components/UI/HookForm/Provider';
import Modal from '../../../components/UI/Modal';
import {
  deliverableStatus,
  milestoneStatus,
  taskStatus,
} from '../../../constants/Project';
import { approvalAction, approvalStatus } from '../../../constants/Approval';
import { notifyUser } from '../../../store/Toast';
import { RefType, UpdateApprovalPayload } from '../../../types/Approvals';
import { InternalTeam, ProjectPhases } from '../../../types/Project';
import {
  ApprovalActionType,
  ApprovalStatusGroupType,
} from '../../../types/Shared';
import { SherpaProjectReactQueryKey } from '../../../constants/ReactQueryKeys';
import { getProjectTeamMembers } from '../../../api/Sherpa/Project/modules/ProjectTeam';

interface UpdateParams {
  approvalStatus: ApprovalActionType;
  refId: number;
}

type RefType2 = "Deliverable" | "Task" | "Milestone" 

type RefTypeAPI = {
  [K in RefType2]: {
    get: (id: number) => Promise<any>;
    update: (params: UpdateParams) => Promise<any>;
  };
};

type RefTypeStatus = {
  [K in RefType2]: { [id: string]: string };
};

const refTypeAPI: RefTypeAPI = {
  Deliverable: {
    get: getDeliverable,
    update: async (params) => {
      const { approvalStatus, refId } = params;
      let status = '';
      if (approvalStatus === 'APPROVED') {
        status = 'COMPLETE';
      } else if (approvalStatus === 'REJECTED') {
        status = 'IN_PROGRESS';
      } else {
        status = 'UNDER_REVIEW';
      }
      if (status) {
        return updateTdm({
          data: { status },
          tdmId: refId,
          type: 'deliverables',
        });
      }
    },
  },
  Task: {
    get: getTask,
    update: async (params) => {
      const { approvalStatus, refId } = params;
      let status = '';
      if (approvalStatus === 'APPROVED') {
        status = 'COMPLETE';
      } else if (approvalStatus === 'REJECTED') {
        status = 'NOT_STARTED';
      }
      if (status) {
        return updateTdm({
          data: { status },
          tdmId: refId,
          type: 'tasks',
        });
      }
    },
  },
  Milestone: {
    get: getMilestone,
    update: async (params) => {
      const { approvalStatus, refId } = params;
      let status = '';
      if (approvalStatus === 'APPROVED') {
        status = 'COMPLETE';
      } else if (approvalStatus === 'REJECTED') {
        status = 'IN_PROGRESS';
      } else {
        status = 'UNDER_REVIEW';
      }
      if (status) {
        return updateTdm({
          data: { status },
          tdmId: refId,
          type: 'milestones',
        });
      }
    },
  },
};

const refTypeStatus: RefTypeStatus = {
  Deliverable: deliverableStatus,
  Task: taskStatus,
  Milestone: milestoneStatus,
};

type ApprovalActionIcons = {
  [K in ApprovalActionType]: JSX.Element;
};
const approvalActionIcons: ApprovalActionIcons = {
  APPROVED: <CheckIcon className='h-4 w-4' />,
  REJECTED: <BanIcon className='h-4 w-4' />,
  DELEGATED: <UsersIcon className='h-4 w-4' />,
};

interface TDMDetails {
  category: string;
  lead: { name: string; profileImageUrl: string; id: number };
  support: string;
  phase: string;
  vendor: { name: string; id: number };
  project: { name: string; id: number };
  status: string;
  notes: string;
}

export const ApprovalDetails = () => {
  const { approvalId: id = 0 } = useParams();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [showUserModal, setShowUserModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showRejectModal, setShowRejectModal] = useState<boolean>(false);
  const [showApproveModal, setShowApproveModal] = useState<boolean>(false);

  const toggleUserModal = () => {
    setShowUserModal((prev) => !prev);
  };

  const toggleEditModal = () => {
    setShowEditModal((prev) => !prev);
  };

  const toggleRejectionModal = () => {
    setShowRejectModal((prev) => !prev);
  };

  const toggleApprovalModal = () => {
    setShowApproveModal((prev) => !prev);
  };

  const approvalQuery = useQuery(
    ['approvals', id],
    async () => {
      const response = await getApprovalById(id as number);
      return response.data;
    },
    {
      refetchInterval: 60000,
      enabled: !!id,
    }
  );

  const postComments = useCallback(
    async (values: { comment: string }) => {
      const response = await postComment({
        data: {
          resource_id: Number(id),
          resource_type: 'Approval',
          ...values,
        },
      });
      return response;
    },
    [id]
  );

  const getComments = useCallback(
    (_params: GetCommentParams) => {
      return getPartnerProposalComments({
        params: {
          resource_id: Number(id),
          resource_type: 'Approval',
        },
      });
    },
    [id]
  );

  const { data } = approvalQuery;
  const {
    title,
    refType,
    refId,
    desc,
    version,
    createdBy,
    assignUser,
    status: approvalStatusValue = '',
  } = data || {};

  const updateApprovalMutation = useMutation(
    async (payload: UpdateApprovalPayload) => {
      const { status } = payload;
      await updateApproval(payload);
      if (status && refId) {
        await refTypeAPI[refType as keyof typeof refTypeAPI]['update']({
          refId,
          approvalStatus: status,
        });
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['approvals']);
        queryClient.invalidateQueries(['other-versions']);
        queryClient.invalidateQueries(['ref-query']);
        queryClient.invalidateQueries(['approval-comments']);
        setShowUserModal(false);
        setShowEditModal(false);
        setShowApproveModal(false);
        setShowRejectModal(false);
      },
      onError: () => {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `Failed to update Approval`,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const { mutate: mutateApproval, isLoading: updatingApproval } =
    updateApprovalMutation;

  const approvalStatusText =
    approvalStatus[approvalStatusValue as keyof ApprovalStatusGroupType];
  const getUrl = () => {
    switch (refType) {
      case 'Deliverable':
        return `/app/dashboard/deliverables/${refId}`;
      case 'Task':
        return `/app/dashboard/tasks/${refId}`;
      case 'Milestone':
        return `/app/dashboard/milestones/${refId}`;
      default:
        return '';
    }
  };

  const otherVersionsQuery = useQuery(
    ['other-versions', refType, refId, version],
    async () => {
      const response = await getApprovals({ refId, refType });
      return response.data;
    },
    {
      enabled: !!refType && !!refId && !!version,
    }
  );

  const { data: otherVersions = [] } = otherVersionsQuery;

  const tdmQuery = useQuery(
    ['ref-query', refType, refId],
    async () => {
      const response = await refTypeAPI[refType as keyof RefTypeAPI]['get'](
        refId || 0
      );
      return response.data;
    },
    {
      refetchInterval: 60000,
      enabled: !!refType && !!refId,
    }
  );

  const { data: tdmData = {} } = tdmQuery;
  const getDetails = (): TDMDetails => {
    switch (refType) {
      case 'Deliverable':
        return {
          category: tdmData.category || 'None',
          support: tdmData.support?.name || 'None',
          phase: tdmData.phase,
          vendor: tdmData.vendor || 'None',
          project: tdmData.project || { name: 'None', id: 0 },
          status: tdmData.status,
          notes: tdmData.notes || 'None',
          lead: tdmData.lead,
        };
      case 'Milestone':
        return {
          category: tdmData.category || 'None',
          support: tdmData.support?.name || 'None',
          phase: tdmData.phase,
          vendor: tdmData.vendor || 'None',
          project: tdmData.project || { name: 'None', id: 0 },
          status: tdmData.status,
          notes: tdmData.notes || 'None',
          lead: tdmData.lead,
        };
      case 'Task':
        return {
          category: tdmData.category || 'None',
          support: tdmData.support?.name || 'None',
          phase: tdmData.phase,
          vendor: tdmData.vendor || 'None',
          project: tdmData.project || { name: 'None', id: 0 },
          status: tdmData.status,
          notes: tdmData.notes || 'None',
          lead: tdmData.lead,
        };
      default:
        return {
          category: 'unknown',
          support: 'unknown',
          phase: 'unknown',
          vendor: {
            name: 'unknown',
            id: 0,
          },
          project: {
            name: 'unknown',
            id: 0,
          },
          status: 'unknown',
          lead: {
            name: 'unknown',
            profileImageUrl: '',
            id: 0,
          },
          notes: 'unknown',
        };
    }
  };

  const {
    category,
    support,
    vendor,
    phase,
    project,
    status,
    lead: { name, id: leadId } = {
      name: '',
      profileImageUrl: '',
      id: 0,
    },
  } = getDetails();
  const teamMembersQuery = useQuery(
    [SherpaProjectReactQueryKey.GET_PROJECT_MEMBERS, project.id, phase],
    async () => {
      const response = await getProjectTeamMembers({
        projectId: project.id,
        params: { phase: phase.toLowerCase() as ProjectPhases },
      });
      return response.data?.internal || [];
    },
    {
      enabled: !!project.id && !!phase,
    }
  );

  const { data: teamMembers = [] } = teamMembersQuery;

  const menuItems: DropDownMenuItem[] = Object.keys(approvalAction).map(
    (key) => {
      return {
        clicked: () => {
          if (key === 'DELEGATED') {
            toggleUserModal();
          } else if (key === 'REJECTED') {
            toggleRejectionModal();
          } else if (key === 'APPROVED') {
            toggleApprovalModal();
          }
        },
        name: approvalAction[key as keyof typeof approvalAction],
        icon: approvalActionIcons[key as keyof typeof approvalActionIcons],
      };
    }
  );

  const handleSubmit = ({
    assignedUser,
    note,
  }: {
    assignedUser: InternalTeam;
    note: string;
  }) => {
    mutateApproval({
      id: id as number,
      status: 'DELEGATED',
      assignedUserId: assignedUser.id,
      note,
    });
  };

  const handleRejection = ({ note }: { note: string }) => {
    mutateApproval({
      id: id as number,
      status: 'REJECTED',
      note,
    });
  };

  const handleApproval = () => {
    mutateApproval({
      id: id as number,
      status: 'APPROVED',
    });
  };

  const handleApprovalEdit = ({
    title,
    desc,
  }: {
    title: string;
    desc: string;
  }) => {
    mutateApproval({
      id: id as number,
      title,
      desc,
    });
  };

  return (
    <TdmLayout title='Approval Detail'>
      <TdmLeftLayout>
        <LayoutCard>
          <LayoutCardTitle
            title={title || ''}
            actions={
              <div className='flex gap-2 items-center'>
                {approvalStatusValue !== 'REJECTED' &&
                  approvalStatusValue !== 'DELEGATED' && (
                    <DropDown menuItems={menuItems}>
                      <div className='flex gap-2 items-center border p-2 rounded-md w-40 justify-between font-medium'>
                        <span>Actions</span>
                        <ChevronDownIcon className='h-4 w-4' />
                      </div>
                    </DropDown>
                  )}
                <EditIconButton size='w-10 h-10' onClick={toggleEditModal} />
              </div>
            }
          />
          <LayoutCardContent>
            {assignUser?.name && <TdmLead title='' name={assignUser?.name} />}
            <TdmInfoGroup>
              <TdmInfo title='Team Lead' bold>
                {name || 'None'}
              </TdmInfo>
              <TdmInfo title='Created By' bold>
                {createdBy?.name || 'None'}
              </TdmInfo>
              <TdmInfo title='Phase' bold>
                <p className='capitalize'>{phase}</p>
              </TdmInfo>
              <TdmInfo title='Type' bold>
                {refType}
              </TdmInfo>
              <TdmInfo title='Approval Status' bold>
                <Badge status={approvalStatusValue} text={approvalStatusText} />
              </TdmInfo>
              <TdmInfo title='Category' bold>
                {category}
              </TdmInfo>
              <TdmInfo title='Support' bold>
                {support}
              </TdmInfo>
              <TdmInfo title='Vendor' bold>
                {vendor.name}
              </TdmInfo>
              <TdmInfo title='Project' bold>
                {project.name}
              </TdmInfo>
              <TdmInfo title='Status'>
                <Badge
                  status={status}
                  text={
                    (refTypeStatus[refType as keyof RefTypeStatus] || {})[
                      status
                    ]
                  }
                />
              </TdmInfo>
              <TdmInfo title='Description' noBorder>
                {desc || 'None'}
              </TdmInfo>
            </TdmInfoGroup>
          </LayoutCardContent>
        </LayoutCard>
        <Comments
          postComments={postComments}
          getComments={getComments}
          queryKey={`approval-comments`}
          dependency={[id]}
        />
      </TdmLeftLayout>
      <TdmRightLayout>
        <LayoutCard>
          <LayoutCardTitle title={'More Info'} />
          <LayoutCardContent>
            <div className='grid grid-cols-1 gap-2'>
              <div>
                <a
                  href={getUrl()}
                  target='_blank'
                  rel='noreferrer'
                  className='text-primaryColor'
                >
                  View {refType} Details
                </a>
              </div>
              <p className='font-bold'>Selected Version:</p>
              <div className='flex gap-2'>
                {version}{' '}
                <Badge status={approvalStatusValue} text={approvalStatusText} />
              </div>
              <p className='font-bold'>Version History:</p>
              {otherVersions.length === 0 && 'N/A'}
              {otherVersions.map((history) => {
                return (
                  <Link
                    key={history.id}
                    to={`/app/dashboard/approvals/${history.id}`}
                  >
                    <div className=' flex gap-2'>
                      {history.version}
                      <Badge
                        status={history.status}
                        text={approvalStatus[history.status]}
                      />
                    </div>
                  </Link>
                );
              })}
            </div>
          </LayoutCardContent>
        </LayoutCard>
      </TdmRightLayout>
      {showUserModal && (
        <Modal>
          <HookFormProvider>
            <HookForm onSubmit={handleSubmit}>
              <HookReactSingleSelect
                label='Delegate'
                name='assignedUser'
                required
                options={teamMembers}
                getOptionLabel={(option) => (option as InternalTeam).name}
                getOptionValue={(option) => `${(option as InternalTeam).id}`}
                isOptionDisabled={(option) => {
                  const teamMember = option as InternalTeam;
                  if (teamMember.id === leadId) {
                    return true;
                  }
                  return false;
                }}
              />
              <div className='mt-4'>
                <HookTextarea name='note' label='Reason for Delegation' />
              </div>
              <div className='flex gap-2 justify-end w-full mt-5'>
                <Button
                  label={`Cancel`}
                  btnStyle='btn-neutral'
                  btnType='button'
                  disabled={updateApprovalMutation.isLoading}
                  onClick={() => {
                    toggleUserModal();
                  }}
                />
                <Button
                  label={`${
                    updateApprovalMutation.isLoading
                      ? 'Please Wait...'
                      : 'Delegate'
                  }`}
                  btnStyle='btn-primary'
                  btnType='submit'
                  disabled={updateApprovalMutation.isLoading}
                />
              </div>
            </HookForm>
          </HookFormProvider>
        </Modal>
      )}

      {showEditModal && (
        <Modal>
          <HookFormProvider
            defaultValues={{
              title,
              desc,
            }}
          >
            <HookForm onSubmit={handleApprovalEdit}>
              <HookInput name='title' label='Title' required type='text' />
              <HookTextarea name='desc' label='Description' required />
              <div className='flex gap-2 justify-end w-full mt-5'>
                <Button
                  label={`Cancel`}
                  btnStyle='btn-neutral'
                  btnType='button'
                  disabled={updateApprovalMutation.isLoading}
                  onClick={() => {
                    toggleEditModal();
                  }}
                />
                <Button
                  label={`${
                    updateApprovalMutation.isLoading
                      ? 'Please Wait...'
                      : 'Update'
                  }`}
                  btnStyle='btn-primary'
                  btnType='submit'
                  disabled={updateApprovalMutation.isLoading}
                />
              </div>
            </HookForm>
          </HookFormProvider>
        </Modal>
      )}
      {showRejectModal && (
        <Modal>
          <HookFormProvider>
            <HookForm onSubmit={handleRejection}>
              <HookTextarea name='note' label='Reason for Rejection' required />
              <div className='flex gap-2 justify-end w-full mt-5'>
                <Button
                  label={`Cancel`}
                  btnStyle='btn-neutral'
                  btnType='button'
                  disabled={updateApprovalMutation.isLoading}
                  onClick={() => {
                    toggleRejectionModal();
                  }}
                />
                <Button
                  label={`${
                    updateApprovalMutation.isLoading
                      ? 'Please Wait...'
                      : 'Reject'
                  }`}
                  btnStyle='btn-primary'
                  btnType='submit'
                  disabled={updateApprovalMutation.isLoading}
                />
              </div>
            </HookForm>
          </HookFormProvider>
        </Modal>
      )}
      {showApproveModal && (
        <Modal>
          <HookFormProvider>
            <HookForm onSubmit={handleApproval}>
              <p>Are you sure?</p>
              <div className='flex gap-2 justify-end w-full mt-5'>
                <Button
                  label='No'
                  btnStyle='btn-neutral'
                  btnType='button'
                  disabled={updateApprovalMutation.isLoading}
                  onClick={() => {
                    toggleApprovalModal();
                  }}
                />
                <Button
                  label={`${
                    updateApprovalMutation.isLoading ? 'Please Wait...' : 'Yes'
                  }`}
                  btnStyle='btn-primary'
                  btnType='submit'
                  disabled={updateApprovalMutation.isLoading}
                />
              </div>
            </HookForm>
          </HookFormProvider>
        </Modal>
      )}
    </TdmLayout>
  );
};
